import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import useToast from "~/components/ui/toast";
import type { PartialAgent } from "~/services/agents";
import {
  cancelConversation,
  type ConversationConnect,
  conversationConnectPath,
} from "~/services/conversations";
import type { PartialSkill } from "~/services/skills";
import { apiClient } from "~/utils/apiClient";
import { AssistantTurn } from "./AssistantTurn";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import { UserMessage } from "./UserMessage";
import { Composer } from "./composer/Composer";
import { useAgent } from "./useAgent";
import { useStreamingTurnIds } from "./useStreamingTurnIds";
import { SubagentEventsProvider, useSubagentEvents } from "./useSubagentEvents";
import { useTurns } from "./useTurns";

// Distance from the bottom (px) past which we stop auto-following new
// content and surface the jump-to-bottom button instead.
const BOTTOM_THRESHOLD = 100;

interface ChatProps {
  agent: ReplayHttpAgent;
  conversationId: string;
  // Identity of the agent bound to this conversation — used to render the
  // assistant-message avatar consistently with the rest of the app.
  conversationAgent: PartialAgent | null;
  // True when /connect reported MESSAGE_IN_PROGRESS — attach to the server's
  // in-flight SSE stream on mount instead of starting a new run.
  resumeInFlight: boolean;
  // Skills available to the composer's slash-command picker; fetched at the
  // conversation level so a new conversation refreshes the list.
  skills: PartialSkill[];
}

export const Chat = ({
  agent: rawAgent,
  conversationId,
  conversationAgent,
  resumeInFlight,
  skills,
}: ChatProps) => {
  const agent = useAgent(rawAgent) as ReplayHttpAgent;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showError } = useToast();

  const [showJump, setShowJump] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const userMsgIdToTurnId = useStreamingTurnIds(agent);
  const subagentEvents = useSubagentEvents(agent);
  const turns = useTurns(agent.messages, agent.isRunning, userMsgIdToTurnId);

  const recomputeJump = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > BOTTOM_THRESHOLD);
  }, []);

  // Follow new content only when the user is already near the bottom.
  // If they've scrolled up to read history, leave their position alone
  // and let the jump button bring them back when they want.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance < BOTTOM_THRESHOLD) {
      el.scrollTo({ top: el.scrollHeight });
    }
    recomputeJump();
  }, [turns, recomputeJump]);

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Tear down any in-flight SSE on unmount, regardless of how the run was
  // started. The replay attached here only fires when we mounted mid-run,
  // but the cleanup must also cover normal user-initiated runs started later
  // via handleSubmit — otherwise navigating away leaks the stream.
  // abortRun() is a no-op when nothing is running, so the unconditional
  // cleanup is safe in both paths.
  useEffect(() => {
    if (resumeInFlight) {
      agent.enableReplayForNextRun();
      void agent.runAgent();
    }
    return () => {
      agent.abortRun();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const handleSubmit = async (text: string) => {
    // A new submit supersedes any in-flight cancel reconcile. Without this
    // the reconcile effect aborts when isRunning flips back to true and
    // never clears isCancelling, leaving the composer stuck on "Cancelling…"
    // for the whole new run.
    setIsCancelling(false);
    agent.addMessage({
      id: crypto.randomUUID(),
      role: "user",
      content: text,
    });
    await agent.runAgent();
  };

  // When the SSE stream ends while we were trying to cancel, we have no
  // reliable signal from the server that the cancel actually landed: the
  // analysis-agent closes the stream the same way on cancel as on normal
  // completion, and the connection can also end for unrelated reasons
  // (LB timeout, network blip). Instead of guessing, refetch `/connect`
  // and adopt whatever it says.
  //
  //   - READY            → server reached a quiescent state; trust the
  //                        returned message list (which includes the
  //                        persisted "[cancelled]" marker if the cancel
  //                        actually took, or the completed turn if it
  //                        finished naturally first).
  //   - MESSAGE_IN_PROGRESS → cancel never landed and the background task
  //                        is still running. Re-attach to its SSE replay
  //                        so the user sees the rest of the response
  //                        rather than a frozen partial.
  useEffect(() => {
    if (agent.isRunning || !isCancelling) return;
    const controller = new AbortController();
    apiClient<ConversationConnect>(conversationConnectPath(conversationId), {
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        agent.setMessages(data.messages);
        if (data.status === "MESSAGE_IN_PROGRESS") {
          agent.enableReplayForNextRun();
          void agent.runAgent();
        }
        setIsCancelling(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Best-effort reconciliation: log and clear the flag so the user
        // can retry or refresh themselves.
        console.error("Failed to reconcile conversation after cancel", err);
        setIsCancelling(false);
      });
    return () => controller.abort();
  }, [agent, agent.isRunning, isCancelling, conversationId]);

  const handleCancel = useCallback(async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      await cancelConversation(conversationId);
    } catch (err) {
      // Reset so the user can try again; the server-side run is still going.
      setIsCancelling(false);
      console.error("Failed to cancel conversation", err);
      showError("Couldn't stop generation. Try again in a moment.");
    }
  }, [conversationId, isCancelling, showError]);

  return (
    <SubagentEventsProvider value={subagentEvents}>
      <div className="flex flex-1 flex-col overflow-hidden">
        <div ref={scrollRef} onScroll={recomputeJump} className="flex-1 overflow-y-auto pt-4">
          <ol className="mx-auto flex max-w-[60rem] flex-col gap-4 px-6 pb-4" role="log">
            {turns.map((turn) =>
              turn.kind === "user" ? (
                <UserMessage key={turn.key} text={turn.text} />
              ) : (
                <AssistantTurn
                  key={turn.key}
                  turn={turn}
                  agent={conversationAgent}
                  conversationId={conversationId}
                  isCancelling={isCancelling}
                />
              )
            )}
          </ol>
          <div
            aria-hidden
            className="pointer-events-none sticky bottom-0 h-8 bg-gradient-to-t from-background to-transparent"
          />
        </div>

        <div className="relative px-4 pb-4">
          {showJump && (
            <button
              type="button"
              onClick={jumpToBottom}
              aria-label={agent.isRunning ? "Jump to current response" : "Jump to bottom"}
              className="absolute bottom-full left-1/2 z-20 mb-3 flex size-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground"
            >
              {agent.isRunning ? <TypingDots /> : <ChevronDown className="size-4" aria-hidden />}
            </button>
          )}
          <Composer
            isRunning={agent.isRunning}
            isCancelling={isCancelling}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            skills={skills}
          />
        </div>
      </div>
    </SubagentEventsProvider>
  );
};

const TypingDots = () => (
  <span className="flex items-end gap-0.5" aria-hidden>
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "0ms" }}
    />
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "150ms" }}
    />
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "300ms" }}
    />
  </span>
);
